import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  ExternalLink, 
  Zap, 
  TrendingDown, 
  Clock, 
  Target,
  Cpu,
  Database,
  GitBranch,
  Layers
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import ComprehensiveFooter from '../ComprehensiveFooter';

const EnergyUseCase = () => {
  const keyMetrics = [
    { label: 'Cost Reduction', value: '90%', icon: TrendingDown, color: 'text-green-400' },
    { label: 'Time Reduction', value: '18h → 2.5h', icon: Clock, color: 'text-blue-400' },
    { label: 'Hit Rate Increase', value: '8.3%', icon: Target, color: 'text-emerald-400' },
    { label: 'Search Space Pruned', value: '2.6M → 50', icon: Database, color: 'text-purple-400' }
  ];

  const technicalSpecs = [
    {
      category: 'Quantum Processing',
      items: [
        'Rigetti Aspen‑M 80Q superconducting QPU',
        'QAOA (Quantum Approximate Optimization Algorithm)',
        'Cirq quantum circuit framework',
        '2,000+ shots per quantum job'
      ]
    },
    {
      category: 'Classical Integration',
      items: [
        'GPU-accelerated forward modeling (H200 cluster)',
        'Kubernetes orchestrated QPU sidecars',
        'Redis caching for parameter optimization',
        'gRPC quantum-classical mediation layer'
      ]
    },
    {
      category: 'Infrastructure',
      items: [
        'Flatcar 4020.2.1 container nodes',
        'Helm-deployed QPU gateways',
        'mTLS security via Linkerd',
        'Idempotent replay for job resilience'
      ]
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: 'Seismic Data Ingestion',
      description: 'High-resolution geological survey data processed through preprocessing pipelines',
      icon: Database
    },
    {
      step: 2,
      title: 'Parameter Space Definition',
      description: 'Define combinatorial search space for strata thickness, fault probability, porosity, and pressure parameters',
      icon: Layers
    },
    {
      step: 3,
      title: 'Quantum Optimization',
      description: 'QAOA circuits solve combinatorial subset selection on Rigetti Aspen‑M 80Q',
      icon: Cpu
    },
    {
      step: 4,
      title: 'Classical Validation',
      description: 'GPU clusters perform coarse‑grained forward modeling on quantum-selected candidates',
      icon: GitBranch
    },
    {
      step: 5,
      title: 'Site Selection',
      description: 'Top 50 candidate strata identified for drilling operations with confidence metrics',
      icon: Target
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,215,0,0.1),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <Badge className="mb-6 bg-yellow-500/10 text-yellow-300 border-yellow-500/20">
              Energy: Oil & Gas Exploration
            </Badge>
            <h1 className="display-large mb-6 text-white">
              Quantum QAOA + GPU Geophysics
            </h1>
            <h2 className="heading-1 mb-8 text-yellow-300">
              Slashing Sub‑Surface Search Space
            </h2>
            <p className="body-large text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Revolutionary approach combining quantum optimization with classical GPU computing for seismic inversion 
              and drilling-site selection, achieving 90% cost reduction and 7× speed improvement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-gradient-to-r from-yellow-900/10 to-orange-900/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyMetrics.map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-6 text-center border-white/10 bg-black/40">
                    <IconComponent className={`w-8 h-8 mx-auto mb-4 ${metric.color}`} />
                    <div className={`text-2xl font-bold mb-2 ${metric.color}`}>
                      {metric.value}
                    </div>
                    <div className="text-gray-300 text-sm">{metric.label}</div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Challenge */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 border-red-500/20 bg-red-500/5">
                <h3 className="heading-2 mb-6 text-white flex items-center gap-3">
                  <Zap className="w-6 h-6 text-red-400" />
                  The Challenge
                </h3>
                <div className="space-y-4 text-gray-300">
                  <p className="leading-relaxed">
                    Seismic inversion and drilling‑site selection require scanning a vast combinatorial set of 
                    sub‑surface parameter grids including strata thickness, fault probability, porosity, 
                    and pressure measurements.
                  </p>
                  <p className="leading-relaxed">
                    Classical heuristic search with 64×H200 cluster costs <strong className="text-yellow-400">USD 250k per run</strong> 
                    and delivers results in <strong className="text-yellow-400">18 hours</strong>—too slow for 
                    on‑the‑fly decision‑making during exploratory drilling operations.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mt-6">
                    <div className="text-red-300 font-semibold mb-2">Critical Limitations:</div>
                    <ul className="space-y-1 text-sm">
                      <li>• 2.6 × 10⁶ parameter combinations to evaluate</li>
                      <li>• $250,000 computational cost per analysis</li>
                      <li>• 18-hour turnaround time</li>
                      <li>• Limited real-time decision capability</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Solution */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 border-emerald-500/20 bg-emerald-500/5">
                <h3 className="heading-2 mb-6 text-white flex items-center gap-3">
                  <Cpu className="w-6 h-6 text-emerald-400" />
                  The Quantum Solution
                </h3>
                <div className="space-y-4 text-gray-300">
                  <p className="leading-relaxed">
                    Hybrid approach that keeps coarse‑grained forward modeling on GPUs while 
                    off‑loading <strong className="text-emerald-400">combinatorial subset selection</strong> to 
                    QAOA (Quantum Approximate Optimization Algorithm) circuits running on Rigetti's Aspen‑M 80Q.
                  </p>
                  <p className="leading-relaxed">
                    The quantum processing unit operates as a specialized sidecar, 
                    solving optimization problems in <strong className="text-emerald-400">2.5 hours</strong> 
                    while reducing costs by <strong className="text-emerald-400">90%</strong>.
                  </p>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mt-6">
                    <div className="text-emerald-300 font-semibold mb-2">Quantum Advantages:</div>
                    <ul className="space-y-1 text-sm">
                      <li>• 80-qubit superconducting quantum processor</li>
                      <li>• QAOA optimization for combinatorial problems</li>
                      <li>• 12.8ms + 5μs + 3ms per quantum call</li>
                      <li>• 40% cost reduction through intelligent caching</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Architecture */}
      <section className="py-16 bg-gradient-to-r from-purple-900/10 to-blue-900/10">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-1 mb-6 text-white">Technical Architecture</h2>
            <p className="body-large text-gray-300 max-w-3xl mx-auto">
              A sophisticated hybrid quantum-classical system designed for enterprise-scale geophysical analysis
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {technicalSpecs.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 h-full border-white/10 bg-black/40">
                  <h3 className="heading-3 mb-4 text-white">{spec.category}</h3>
                  <ul className="space-y-3">
                    {spec.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-gray-300 text-sm">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-1 mb-6 text-white">Quantum-Enhanced Workflow</h2>
            <p className="body-large text-gray-300 max-w-3xl mx-auto">
              From raw geological data to optimized drilling sites in 2.5 hours
            </p>
          </motion.div>

          <div className="space-y-8">
            {workflowSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex items-center gap-8 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    {step.step}
                  </div>
                  <Card className="flex-1 p-6 border-white/10 bg-black/40 flex items-center gap-4">
                    <IconComponent className="w-8 h-8 text-yellow-400 flex-shrink-0" />
                    <div>
                      <h3 className="heading-3 mb-2 text-white">{step.title}</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-16 bg-gradient-to-r from-green-900/10 to-emerald-900/10">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-1 mb-6 text-white">Real-World Impact</h2>
            <p className="body-large text-gray-300 max-w-3xl mx-auto">
              Proven results from production deployment in energy exploration
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Card className="p-8 border-emerald-500/20 bg-emerald-500/5">
              <h3 className="heading-2 mb-6 text-white">Quantified Outcomes</h3>
              <div className="space-y-6">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-300">Search space reduction</span>
                  <span className="text-emerald-400 font-bold">2.6M → 50 candidates</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-300">Processing time</span>
                  <span className="text-emerald-400 font-bold">18h → 2.5h (7× faster)</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-300">Cost savings</span>
                  <span className="text-emerald-400 font-bold">90% reduction</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-300">Site-B-42 hit rate improvement</span>
                  <span className="text-emerald-400 font-bold">8.3% higher</span>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-blue-500/20 bg-blue-500/5">
              <h3 className="heading-2 mb-6 text-white">Technical Performance</h3>
              <div className="space-y-6">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-300">Quantum job latency</span>
                  <span className="text-blue-400 font-bold">12.8ms network + 5μs QPU</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-300">QAOA variance control</span>
                  <span className="text-blue-400 font-bold">&gt;2,000 shots required</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-300">Caching efficiency</span>
                  <span className="text-blue-400 font-bold">40% QPU cost reduction</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-300">mTLS overhead</span>
                  <span className="text-blue-400 font-bold">~0.9ms (acceptable)</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-900/20 to-orange-900/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-2 mb-4 text-white">Ready to Transform Your Energy Operations?</h2>
            <p className="body-large text-gray-300 mb-8">
              Join the quantum revolution in energy exploration. Reduce costs by 90% and accelerate discoveries with our proven quantum-classical hybrid systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="btn-primary px-8 py-4"
                onClick={() => window.open('https://dyota.substack.com/p/applied-quantum-computing-case-study', '_blank')}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Read Full Case Study
              </Button>
              <Button className="btn-secondary px-8 py-4">
                Schedule Energy Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <ComprehensiveFooter />
    </div>
  );
};

export default EnergyUseCase;